import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import CustomizedSwitch from '../../../components/Switch';
import StatusBadge from '../../../components/StatusBadge';
import RenewalModal from '../../../components/RenewalModal';
import PlanChangeModal from '../../../components/PlanChangeModal';
import SubscriptionService, { SubscriptionFormPayload } from '../../../services/subscription.service';
import TenantService from '../../../services/tenant.service';
import PlanService from '../../../services/plan.service';
import ProductService from '../../../services/product.service';
import MasterLookupService from '../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../enums';

interface TenantOption {
  id: number;
  tenant_name: string;
  product_id: number;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface PlanOption {
  id: number;
  plan_code: string;
  plan_name: string;
  product_id: number;
  billing_cycle_id: number;
  billing_cycle_name?: string;
  price: string;
  currency_id: number;
}

interface CurrencyOption {
  id: number;
  currency_code: string;
  symbol: string;
}

interface SubscriptionFormState {
  tenant_id: number | '';
  product_id: number | '';
  plan_id: number | '';
  billing_cycle_id: number | '';
  start_date: string;
  end_date: string;
  amount: number | string;
  currency_id: number | '';
  auto_reminder: boolean;
  notes: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const initialForm: SubscriptionFormState = {
  tenant_id: '',
  product_id: '',
  plan_id: '',
  billing_cycle_id: '',
  start_date: today(),
  end_date: '',
  amount: '',
  currency_id: '',
  auto_reminder: true,
  notes: '',
};

const SubscriptionForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const edit = !!id;

  const [formData, setFormData] = useState<SubscriptionFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [subscriptionNo, setSubscriptionNo] = useState<string | null>(null);
  const [existingActiveId, setExistingActiveId] = useState<number | null>(null);
  const [statusInfo, setStatusInfo] = useState<{
    code: string | null;
    name: string | null;
    colour: string | null;
  }>({ code: null, name: null, colour: null });
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<{ newPlanName: string; effectiveDate: string } | null>(null);

  useEffect(() => {
    TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setTenants(response?.data?.items ?? []);
    });
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    PlanService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setPlans(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'currencies', includeDeleted: false, limit: 100 }).then((response: any) => {
      setCurrencies(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      const tenantIdParam = searchParams.get('tenantId');
      setFormData({ ...initialForm, tenant_id: tenantIdParam ? Number(tenantIdParam) : '' });
      return;
    }
    setFetching(true);
    SubscriptionService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          tenant_id: row.tenant_id ?? '',
          product_id: row.product_id ?? '',
          plan_id: row.plan_id ?? '',
          billing_cycle_id: row.billing_cycle_id ?? '',
          start_date: String(row.start_date ?? '').slice(0, 10),
          end_date: String(row.end_date ?? '').slice(0, 10),
          amount: row.amount ?? '',
          currency_id: row.currency_id ?? '',
          auto_reminder: row.auto_reminder ?? true,
          notes: row.notes ?? '',
        });
        setSubscriptionNo(row.subscription_no ?? null);
        setStatusInfo({
          code: row.subscription_status_code ?? null,
          name: row.subscription_status_name ?? null,
          colour: row.subscription_status_colour ?? null,
        });
        setPendingPlanChange(row.pendingPlanChange ?? null);
      })
      .finally(() => setFetching(false));
  }, [id, searchParams]);

  const canRenew = ['expiring_soon', 'expired', 'pending'].includes(statusInfo.code ?? '');
  const canChangePlan = statusInfo.code === 'active';

  // AC-2: Product auto-fills from the selected tenant, read-only.
  const handleTenantChange = useCallback(
    (e: any) => {
      const tenantId = e.target.value === '' ? '' : Number(e.target.value);
      const tenant = tenants.find((tItem) => tItem.id === tenantId);
      setFormData((prev) => ({
        ...prev,
        tenant_id: tenantId,
        product_id: tenant ? tenant.product_id : '',
        plan_id: '',
        billing_cycle_id: '',
        amount: '',
        currency_id: '',
        end_date: '',
      }));
      setExistingActiveId(null);
    },
    [tenants]
  );

  const plansForProduct = useMemo(
    () => plans.filter((plan) => !formData.product_id || plan.product_id === formData.product_id),
    [plans, formData.product_id]
  );

  // AC-2/AC-3: selecting a Plan auto-fills Billing Cycle (read-only), suggests Amount/Currency,
  // and triggers the server-computed End Date preview.
  const handlePlanChange = useCallback(
    (e: any) => {
      const planId = e.target.value === '' ? '' : Number(e.target.value);
      const plan = plansForProduct.find((p) => p.id === planId);
      setFormData((prev) => ({
        ...prev,
        plan_id: planId,
        billing_cycle_id: plan ? plan.billing_cycle_id : '',
        amount: plan ? plan.price : prev.amount,
        currency_id: plan ? plan.currency_id : prev.currency_id,
      }));
    },
    [plansForProduct]
  );

  useEffect(() => {
    if (!formData.billing_cycle_id || !formData.start_date) return;
    SubscriptionService.computeEndDate(Number(formData.billing_cycle_id), formData.start_date)
      .then((response: any) => {
        setFormData((prev) => ({ ...prev, end_date: response?.data?.endDate ?? prev.end_date }));
      })
      .catch(() => undefined);
  }, [formData.billing_cycle_id, formData.start_date]);

  const validate = useCallback(
    (data: SubscriptionFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.tenant_id) nextErrors.tenant_id = t('subscriptions.form.pageTexts.fields.tenant.errors.required');
      if (!data.plan_id) nextErrors.plan_id = t('subscriptions.form.pageTexts.fields.plan.errors.required');
      if (!data.start_date) nextErrors.start_date = t('subscriptions.form.pageTexts.fields.startDate.errors.required');
      const amount = Number(data.amount);
      if (!data.amount || !(amount > 0)) nextErrors.amount = t('subscriptions.form.pageTexts.fields.amount.errors.invalid');
      if (!data.currency_id) nextErrors.currency_id = t('subscriptions.form.pageTexts.fields.currency.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const numericFields = ['currency_id'];
    const value = numericFields.includes(name) ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      setExistingActiveId(null);
      try {
        if (edit && id) {
          await SubscriptionService.update(id, {
            plan_id: Number(formData.plan_id),
            start_date: formData.start_date,
            amount: Number(formData.amount),
            currency_id: Number(formData.currency_id),
            auto_reminder: formData.auto_reminder,
            notes: formData.notes || undefined,
          });
          toast.success(<Toast message={t('subscriptions.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          const payload: SubscriptionFormPayload = {
            tenant_id: Number(formData.tenant_id),
            product_id: Number(formData.product_id),
            plan_id: Number(formData.plan_id),
            billing_cycle_id: Number(formData.billing_cycle_id),
            start_date: formData.start_date,
            amount: Number(formData.amount),
            currency_id: Number(formData.currency_id),
            auto_reminder: formData.auto_reminder,
            notes: formData.notes || undefined,
          };
          await SubscriptionService.create(payload);
          toast.success(<Toast message={t('subscriptions.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.home}${AuthRoutes.subscriptions}`);
      } catch (err: any) {
        const existingId = err?.response?.data?.meta?.existingSubscriptionId ?? err?.response?.data?.existingSubscriptionId;
        if (existingId) setExistingActiveId(existingId);
      } finally {
        setLoading(false);
      }
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header d-flex justify-content-between align-items-center">
        <span>{edit ? t('subscriptions.form.pageTexts.editTitle') : t('subscriptions.form.pageTexts.addTitle')}</span>
        <div className="d-flex align-items-center gap-2">
          {statusInfo.name && statusInfo.colour && <StatusBadge label={statusInfo.name} colour={statusInfo.colour} />}
          {canRenew && (
            <Button
              className="btn btn-sm btn-outline-primary"
              text={t('tenants.detail.pageTexts.subscription.renew')}
              onClick={() => setShowRenewalModal(true)}
            />
          )}
          {canChangePlan && !pendingPlanChange && (
            <Button
              className="btn btn-sm btn-outline-secondary"
              text={t('tenants.detail.pageTexts.subscription.changePlan')}
              onClick={() => setShowPlanChangeModal(true)}
            />
          )}
        </div>
      </h5>
      <hr className="m-0" />

      {pendingPlanChange && (
        <div className="alert alert-info d-flex justify-content-between align-items-center m-4 mb-0">
          <span>
            {t('tenants.detail.pageTexts.subscription.planChangeScheduled', {
              currentPlan: plansForProduct.find((p) => p.id === formData.plan_id)?.plan_name ?? '',
              newPlan: pendingPlanChange.newPlanName,
              effectiveDate: pendingPlanChange.effectiveDate,
            })}
          </span>
        </div>
      )}
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        {subscriptionNo && (
          <div className="col-md-3">
            <Input id="subscription_no" name="subscription_no" label={t('subscriptions.form.pageTexts.fields.subscriptionNo.label')} value={subscriptionNo} disabled />
          </div>
        )}

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="tenant_id">
            {t('subscriptions.form.pageTexts.fields.tenant.label')}
          </label>
          <select
            id="tenant_id"
            name="tenant_id"
            className={`form-select ${errors.tenant_id ? 'is-invalid' : ''}`}
            value={formData.tenant_id}
            disabled={edit}
            onChange={handleTenantChange}
          >
            <option value="">{t('subscriptions.form.pageTexts.fields.tenant.placeholder')}</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.tenant_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <Input
            id="product_id"
            name="product_id"
            label={t('subscriptions.form.pageTexts.fields.product.label')}
            value={products.find((p) => p.id === formData.product_id)?.product_name ?? ''}
            disabled
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="plan_id">
            {t('subscriptions.form.pageTexts.fields.plan.label')}
          </label>
          <select
            id="plan_id"
            name="plan_id"
            className={`form-select ${errors.plan_id ? 'is-invalid' : ''}`}
            value={formData.plan_id}
            disabled={edit || !formData.tenant_id}
            onChange={handlePlanChange}
          >
            <option value="">{t('subscriptions.form.pageTexts.fields.plan.placeholder')}</option>
            {plansForProduct.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.plan_code} — {plan.plan_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <Input
            id="billing_cycle_id"
            name="billing_cycle_id"
            label={t('subscriptions.form.pageTexts.fields.billingCycle.label')}
            value={plansForProduct.find((p) => p.id === formData.plan_id)?.billing_cycle_name ?? ''}
            disabled
          />
        </div>

        <div className="col-md-3">
          <Input
            id="start_date"
            name="start_date"
            type="date"
            label={t('subscriptions.form.pageTexts.fields.startDate.label')}
            required
            value={formData.start_date}
            error={!!errors.start_date}
            errorMessage={errors.start_date}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3">
          <Input
            id="end_date"
            name="end_date"
            type="date"
            label={t('subscriptions.form.pageTexts.fields.endDate.label')}
            value={formData.end_date}
            disabled
          />
        </div>

        <div className="col-md-3">
          <Input
            id="amount"
            name="amount"
            type="number"
            min="0.01"
            label={t('subscriptions.form.pageTexts.fields.amount.label')}
            required
            value={formData.amount}
            error={!!errors.amount}
            errorMessage={errors.amount}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label d-block" htmlFor="currency_id">
            {t('subscriptions.form.pageTexts.fields.currency.label')}
          </label>
          <select
            id="currency_id"
            name="currency_id"
            className={`form-select ${errors.currency_id ? 'is-invalid' : ''}`}
            value={formData.currency_id}
            onChange={(e) => handleChange(e, 'currency_id')}
          >
            <option value="">{t('subscriptions.form.pageTexts.fields.currency.placeholder')}</option>
            {currencies.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency.currency_code} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 d-flex align-items-end">
          <CustomizedSwitch
            label={t('subscriptions.form.pageTexts.fields.autoReminder.label')}
            name="auto_reminder"
            checked={formData.auto_reminder}
            onChange={() => setFormData((prev) => ({ ...prev, auto_reminder: !prev.auto_reminder }))}
          />
        </div>

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('subscriptions.form.pageTexts.fields.notes.label')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        {existingActiveId && (
          <div className="col-12">
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>{t('subscriptions.form.pageTexts.activeAlreadyExists')}</span>
              <Button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                text={t('subscriptions.form.pageTexts.viewExisting')}
                onClick={() => navigate(`${Modules.home}${AuthRoutes.subscriptions}/edit/${existingActiveId}`)}
              />
            </div>
          </div>
        )}

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('subscriptions.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.subscriptions}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('subscriptions.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>

      {edit && id && (
        <RenewalModal
          open={showRenewalModal}
          subscriptionId={Number(id)}
          currentAmount={String(formData.amount)}
          currentCurrencyId={Number(formData.currency_id) || 0}
          currentCurrencyCode={currencies.find((c) => c.id === formData.currency_id)?.currency_code ?? ''}
          onClose={() => setShowRenewalModal(false)}
          onRenewed={(newEndDate) => {
            setShowRenewalModal(false);
            setFormData((prev) => ({ ...prev, end_date: newEndDate }));
            setStatusInfo((prev) => ({ ...prev, code: 'active' }));
          }}
        />
      )}

      {edit && id && (
        <PlanChangeModal
          open={showPlanChangeModal}
          subscriptionId={Number(id)}
          productId={Number(formData.product_id)}
          currentPlanId={Number(formData.plan_id)}
          effectiveDate={formData.end_date}
          onClose={() => setShowPlanChangeModal(false)}
          onScheduled={() => {
            setShowPlanChangeModal(false);
            SubscriptionService.get(id).then((response: any) => {
              setPendingPlanChange(response?.data?.pendingPlanChange ?? null);
            });
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionForm;
